/**
 * AddEditPopup component is used to add or edit a computation.
 * Based on the editMode, it will display the form with the data to edit or the form to add a new computation.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Form,
  SimpleItem,
  GroupItem,
  RequiredRule,
  Label,
} from "devextreme-react/form";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import {
  HORUS_COUPON_METHOD,
  HORUS_ACTUARIAL_RATE_RULE,
} from "data/ENUM/add-options";
import { useAuth } from "providers/AuthProvider";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFilterByCurrentGroup } from "hooks/useFilterByCurrentGroup";
import useItemOwner from "hooks/useItemOwner";

export const popupConfigs = {
  add: {
    width: "92vh",
    height: "auto",
    title: "New Computation",
    icon: "add",
  },
  rowEdit: {
    width: "auto",
    height: "auto",
    title: "Edit",
    icon: "edit",
  },
};

const AddEditPopup = ({
  isVisible,
  onConfirm,
  onCancel,
  datasetList,
  inputFormData,
  editMode,
}) => {
  const { companyList, userList } = useAuth();

  // Determine ownership of the item if edit mode is true
  const ownedItem = useItemOwner(editMode ? inputFormData : null, "group");

  // Filter datasets on the current group
  const datasetsFilteredByGroup = useFilterByCurrentGroup(datasetList);
  const datasets = editMode ? datasetList : datasetsFilteredByGroup;

  // Group datasets by user and display the group
  const groupedDatasets = useGroupedItems(datasets, userList, companyList);

  /**
   * Get the initial data for the form.
   * If the editMode is true, return the inputFormData.
   * Otherwise, return the initial data for the form.
   */
  const getInitialData = () => {
    if (editMode) {
      return {
        ...inputFormData,
        useActuarialRateRules:
          inputFormData.actuarialRateRules != null &&
          inputFormData.actuarialRateRules !== 0,
      };
    } else {
      return {
        description: "",
        createdDate: null,
        datasetId: null,
        couponMethod: null,
        actuarialRateRules: null,
        useActuarialRateRules: false,
      };
    }
  };

  /* State variables */
  const [localData, setLocalData] = useState(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const currentConfig = useMemo(
    () => (editMode ? popupConfigs.rowEdit : popupConfigs.add),
    [editMode]
  );

  useEffect(() => {
    setLocalData({
      ...inputFormData,
      useActuarialRateRules:
        inputFormData.actuarialRateRules != null &&
        inputFormData.actuarialRateRules !== 0,
    });
  }, [inputFormData]);

  // Adding an useEffect to update the date when the dataset changes
  useEffect(() => {
    if (localData.datasetId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetId
      );
      if (selectedDataset) {
        setLocalData((prev) => ({
          ...prev,
          datasetCreatedDate: selectedDataset.evaluationDate,
        }));
      }
    }
  }, [localData.datasetId, datasetList]);

  const handleCancel = () => {
    if (isSubmitting) return;

    if (editMode) {
      setLocalData({
        ...inputFormData,
        useActuarialRateRules:
          inputFormData.actuarialRateRules != null &&
          inputFormData.actuarialRateRules !== 0,
      });
      formRef.current.instance().clear();
    } else {
      setLocalData(getInitialData());
      formRef.current.instance().clear();
    }
    onCancel();
  };

  const handleConfirm = () => {
    if (isSubmitting) return;

    const formInstance = formRef.current.instance();
    const editableFields = [];

    if (editMode) {
      editableFields.push("description");
    } else {
      editableFields.push("description", "datasetId", "couponMethod");
      // Only validate actuarialRateRules if the checkbox is checked
      if (localData.useActuarialRateRules) {
        editableFields.push("actuarialRateRules");
      }
    }

    const validationResult = formInstance.validate(editableFields);

    if (validationResult.isValid) {
      confirmAndReset(localData);
    } else {
      console.log("Invalid fields:", validationResult.brokenRules);
    }
  };

  const confirmAndReset = async (data) => {
    setIsSubmitting(true);
    try {
      // If checkbox is not checked, set actuarialRateRules to 'None' -> 0
      const dataToSend = {
        ...data,
        actuarialRateRules: data.useActuarialRateRules
          ? data.actuarialRateRules
          : 0, // 0 is the default value for none for backend enums
      };
      await onConfirm(dataToSend);
      formRef.current.instance().resetValues();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (e) => {
    setLocalData({ ...localData, [e.dataField]: e.value });
  };

  const handleActuarialRateCheckboxChange = (e) => {
    const newFormData = {
      ...localData,
      useActuarialRateRules: e.value,
    };
    if (!e.value) {
      newFormData.actuarialRateRules = null;
    }
    setLocalData(newFormData);
  };

  return (
    <>
      <PopupWrapper
        {...currentConfig}
        isVisible={isVisible}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        isConfirmDisabled={isSubmitting || (editMode && !ownedItem)}
        isCancelDisabled={isSubmitting}
        showLoading={isSubmitting}
      >
        <Form
          formData={localData}
          ref={formRef}
          onFieldDataChanged={handleFieldChange}
        >
          <GroupItem caption="Informations">
            <SimpleItem
              dataField="description"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: editMode && !ownedItem,
                placeholder: "Enter description",
                onValueChanged: (e) =>
                  setLocalData({ ...localData, description: e.value }),
              }}
              isRequired={true}
            >
              <Label text="Description" />
              <RequiredRule />
            </SimpleItem>

            <SimpleItem
              dataField="datasetCreatedDate"
              name="createdDate"
              editorType="dxDateBox"
              editorOptions={{
                readOnly: true,
                width: "34%",
                displayFormat: "yyyy-MM-dd",
                value: localData.datasetCreatedDate,
              }}
            >
              <Label text="Evaluation Date" />
            </SimpleItem>
          </GroupItem>

          <GroupItem caption="Computation" colCount={4}>
            <SimpleItem
              dataField="datasetId"
              colSpan={4}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: groupedDatasets,
                displayExpr: "description",
                valueExpr: "id",
                searchEnabled: true,
                grouped: true,
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Dataset" />
            </SimpleItem>

            <SimpleItem
              dataField="couponMethod"
              colSpan={4}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: HORUS_COUPON_METHOD,
                displayExpr: "label",
                valueExpr: "id",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Amortization Coupon Method" />
            </SimpleItem>

            {!editMode && (
              <>
                <SimpleItem
                  colSpan={2}
                  dataField="useActuarialRateRules"
                  editorType="dxCheckBox"
                  editorOptions={{
                    readOnly: false,
                    onValueChanged: handleActuarialRateCheckboxChange,
                  }}
                >
                  <Label text="Use Actuarial Rate" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="actuarialRateRules"
                  editorType="dxSelectBox"
                  editorOptions={{
                    dataSource: HORUS_ACTUARIAL_RATE_RULE,
                    displayExpr: "label",
                    valueExpr: "id",
                    readOnly: false,
                    disabled: !localData.useActuarialRateRules,
                    required: localData.useActuarialRateRules,
                  }}
                  isRequired={!editMode}
                >
                  <Label text="Rules to apply" />
                </SimpleItem>
              </>
            )}

            {editMode && (
              <>
                {localData.actuarialRateRules === 0 ? (
                  <SimpleItem
                    colSpan={4}
                    dataField="useActuarialRateRules"
                    editorType="dxCheckBox"
                    editorOptions={{
                      readOnly: true,
                      value: false,
                    }}
                  >
                    <Label text="Use Actuarial Rate" />
                  </SimpleItem>
                ) : (
                  <SimpleItem
                    colSpan={4}
                    dataField="actuarialRateRules"
                    editorType="dxSelectBox"
                    editorOptions={{
                      dataSource: HORUS_ACTUARIAL_RATE_RULE,
                      displayExpr: "label",
                      valueExpr: "id",
                      readOnly: true,
                    }}
                  >
                    <Label text="Actuarial Rate Rule" />
                  </SimpleItem>
                )}
              </>
            )}

            {editMode && (
              <>
                <SimpleItem
                  colSpan={2}
                  dataField="userId"
                  editorType="dxSelectBox"
                  editorOptions={{
                    readOnly: true,
                    dataSource: userList,
                    displayExpr: "displayName",
                    valueExpr: "id",
                  }}
                >
                  <Label text="Created by user" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="groupId"
                  editorType="dxSelectBox"
                  editorOptions={{
                    readOnly: true,
                    dataSource: companyList,
                    displayExpr: "displayName",
                    valueExpr: "id",
                  }}
                >
                  <Label text="Created by company" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="createdAt"
                  editorType="dxDateBox"
                  editorOptions={{
                    readOnly: true,
                    displayFormat: "yyyy-MM-dd",
                    value: inputFormData.createdAt,
                  }}
                >
                  <Label text="Creation Date" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="updatedAt"
                  editorType="dxDateBox"
                  editorOptions={{
                    readOnly: true,
                    displayFormat: "yyyy-MM-dd",
                    value: inputFormData.updatedAt,
                  }}
                >
                  <Label text="Modification Date" />
                </SimpleItem>
              </>
            )}
          </GroupItem>
        </Form>
      </PopupWrapper>
    </>
  );
};

export default AddEditPopup;
